import type { ProductId } from "../lib/products";
import type { Problem } from "./types";

/**
 * The one HTTP layer. Every call the app makes is same-origin (ADR-0046): `/kb/*` for readers,
 * `/api/*` for the consoles, `/bff/*` for sign-in. In development the Vite proxy makes the host
 * same-origin; in a cluster the HTTPRoute does. There is no API origin to configure.
 */

/** Thrown when the host refuses a request, carrying the RFC 9457 problem when there is one. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly problem: Problem | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * A published article this reader may not open. The host answers 403 with `{ title,
 * requiredRole }` and nothing else, so the page can say what is behind the gate.
 */
export class RestrictedError extends Error {
  constructor(
    readonly requiredRole: string,
    readonly title: string,
  ) {
    super(title);
    this.name = "RestrictedError";
  }
}

/**
 * The antiforgery cookie `/bff/user` sets: `__Host-rigo.support.xsrf` deployed, `rigo.support.xsrf` over
 * plain HTTP locally. Never the conventional `XSRF-TOKEN` — production RigoHR sets that one on
 * `.rigohr.com`, so every staff browser sends it here too, and echoing it got every save refused.
 * The host's AntiforgeryMiddleware requires it echoed on every state-changing `/api` call.
 */
const XSRF_COOKIES = ["__Host-rigo.support.xsrf", "rigo.support.xsrf"];
const XSRF_HEADER = "X-XSRF-TOKEN";

export function xsrfToken(): string | undefined {
  const cookies = new Map<string, string>();
  for (const pair of document.cookie.split("; ")) {
    const eq = pair.indexOf("=");
    if (eq > 0 && !cookies.has(pair.slice(0, eq)))
      cookies.set(pair.slice(0, eq), pair.slice(eq + 1));
  }
  for (const name of XSRF_COOKIES) {
    const value = cookies.get(name);
    if (value !== undefined) return decodeURIComponent(value);
  }
  return undefined;
}

/** A query value a caller may leave out; `undefined`, `null` and `""` are simply not sent. */
export type QueryValue = string | number | boolean | null | undefined;

/** Builds `<path>?product=…&…`. The product goes first, so a request log reads by partition. */
export function withQuery(
  path: string,
  product: ProductId | null,
  query: Record<string, QueryValue> = {},
): string {
  const params = new URLSearchParams(product ? { product } : {});
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const suffix = params.toString();
  return suffix ? `${path}?${suffix}` : path;
}

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
};

function messageOf(problem: Problem | null, status: number): string {
  if (problem?.detail) return problem.detail;
  if (problem?.title) return problem.title;
  if (status === 401) return "Your session has ended. Sign in again.";
  if (status === 403) return "You do not have access to this.";
  if (status === 404) return "Not found.";
  return "Something went wrong. Please try again.";
}

async function fail(response: Response): Promise<never> {
  const payload: unknown = await response.json().catch(() => null);

  // A gated article: the only 403 whose body names a rung.
  if (
    response.status === 403 &&
    payload !== null &&
    typeof payload === "object" &&
    typeof (payload as { requiredRole?: unknown }).requiredRole === "string"
  ) {
    const gate = payload as { requiredRole: string; title?: string };
    throw new RestrictedError(gate.requiredRole, gate.title ?? "");
  }

  const problem = payload !== null && typeof payload === "object" ? (payload as Problem) : null;
  throw new ApiError(messageOf(problem, response.status), response.status, problem);
}

async function read<T>(response: Response): Promise<T> {
  if (!response.ok) return fail(response);
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Calls the host with the session cookie and, on a mutation, the antiforgery header. */
export async function apiFetch<T>(
  url: string,
  { method = "GET", body, signal }: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  // Only mutations need the token; sending it on reads would be noise.
  if (method !== "GET") {
    const token = xsrfToken();
    if (token) headers[XSRF_HEADER] = token;
  }

  const response = await fetch(url, {
    method,
    headers,
    credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
  return read<T>(response);
}

/**
 * Multipart POST. Separate from `apiFetch`, which sets a JSON content type and would stop the
 * browser generating the multipart boundary.
 */
export async function apiUpload<T>(url: string, form: FormData): Promise<T> {
  const token = xsrfToken();
  const response = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", ...(token ? { [XSRF_HEADER]: token } : {}) },
    body: form,
  });
  return read<T>(response);
}

/**
 * Fire-and-forget POST for the end-of-read beacon.
 *
 * Uses `sendBeacon` when available because a normal fetch started during `pagehide` is routinely
 * cancelled as the document goes away — which is exactly when this fires. The beacon goes to
 * `/kb`, which takes no antiforgery header (sendBeacon cannot send one).
 */
export function apiBeacon(url: string, body: unknown): void {
  const payload = JSON.stringify(body);

  if (typeof navigator.sendBeacon === "function") {
    navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
    return;
  }

  void fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: payload,
    keepalive: true,
  }).catch(() => {
    // Analytics must never surface an error to a reader.
  });
}

/** The sentence a screen shows for a failed call. */
export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Something went wrong.";
}
